import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, unitByCode, unitsOfDimension, measurementType, bandVerdict, type MeasurementTypeDef, type Target, type Verdict } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { areaLabel, useAreas, type AreaRow } from '../../data/geo.ts'
import { usePersons } from '../../data/people.ts'
import type { MeasurementRow } from '../../data/measure.ts'
import { checkForm } from '../../data/measureUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { formatLocal } from '../../lib/time.ts'
import type { Pending } from '../../sync/merge.ts'
import { Banner, errorText, Field, PendingBanner, Pill } from '../../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'

export const lang2 = (lang: string): 'fr' | 'en' => (lang.startsWith('en') ? 'en' : 'fr')
export const typeLabel = (def: MeasurementTypeDef | undefined, lang: string): string => (def ? def.label[lang2(lang)] : '')

/** The areas a measurement can be taken on (the site itself is not one), as a screen lists them: label, type, code. */
export interface AreaOption { id: string; /** The club's own name for it (many greens are simply "Green"). */ name: string; /** Name and code: what a list shows, so two greens are never the same line. */ label: string; typeCode: string; code: string | null }
export function useAreaOptions(): AreaOption[] | undefined {
  const { i18n } = useTranslation()
  const areas = useAreas()
  return useMemo(() => areas?.filter((a: Pending<AreaRow>) => !a.archived_at && a.area_type_code !== 'site_root')
    .map((a) => { const name = areaLabel(a, i18n.language); return { id: a.id, name, label: a.code && !name.includes(a.code) ? `${name} · ${a.code}` : name, typeCode: a.area_type_code, code: a.code } })
    .sort((x, y) => x.label.localeCompare(y.label, i18n.language, { numeric: true })), [areas, i18n.language])
}

/** Who a reading belongs to, from the device's own copy of the people (looked up once per list, not once per row). */
export function usePersonNames(): (id: string | null | undefined) => string | null {
  const persons = usePersons()
  const names = useMemo(() => new Map((persons ?? []).map((p) => [p.id, p.display_name])), [persons])
  return (id) => (id ? names.get(id) ?? null : null)
}

/** Every unit a person types a type in: those of the canonical unit's dimension, but no affine unit (M24 review: Kelvin exists for the provider's file, which the
 *  server converts; nobody at the club measures in K, and 18 typed as K is −255,15 °C) — save `keep`, the unit a reading already came in (a correction shows its
 *  own unit). The server still accepts and converts any unit of the dimension (ref.convert). */
export const unitsFor = (typeCode: string, keep?: string) => { const d = measurementType(typeCode); const u = d ? unitByCode(d.unit) : undefined; return u ? unitsOfDimension(u.dimension).filter((x) => x.offset === '0' || x.code === keep) : [] }
export const unitSymbol = (code: string, lang: string): string => unitByCode(code)?.symbol[lang2(lang)] ?? code

export function VerdictPill({ verdict, target, unit }: { verdict: Verdict | null; target: Pick<Target, 'low' | 'high'> | undefined; unit: string }) {
  const { t, i18n } = useTranslation()
  if (!verdict || !target) return null
  const band = [target.low !== null ? fmtNum(target.low, i18n.language) : '…', target.high !== null ? fmtNum(target.high, i18n.language) : '…'].join(' – ')
  return <Pill tone={verdict === 'in' ? 'green' : 'amber'}><span data-testid={`verdict-${verdict}`}>{t(`measures.verdict.${verdict}`)} ({band} {unitSymbol(unit, i18n.language)})</span></Pill>
}
export const verdictOf = (value: number, target: Pick<Target, 'low' | 'high'> | undefined) => bandVerdict(value, target)

/** One reading in a list: the value as stored (and as typed, when that was another unit), where, when, with what, and the way to correct it for those who may. */
export function ReadingRow({ r, areaName, who, onChanged }: { r: Pending<MeasurementRow>; areaName: string | null; who?: string | null; onChanged?: () => void }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const manage = useWriteAccess('measurement.manage')
  const def = measurementType(r.measurement_type)
  const [open, setOpen] = useState(false)
  const dec = def?.decimals ?? 2
  const typed = r.unit_entered !== r.unit_code
  return (
    <li className="card flex flex-col gap-1" data-testid="reading-row" data-type={r.measurement_type} data-id={r.id}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">{typeLabel(def, i18n.language)}{areaName ? ` · ${areaName}` : ` · ${t('measures.site')}`}</span>
        <span className="text-xl font-bold" data-testid="reading-value">{fmtNum(r.value, i18n.language, dec)} {unitSymbol(r.unit_code, i18n.language)}</span>
      </div>
      <p className="meta">
        {formatLocal(r.observed_at, site.timezone)}
        {typed && <> · {t('measures.typedAs', { value: fmtNum(r.value_entered, i18n.language, 4), unit: unitSymbol(r.unit_entered, i18n.language) })}</>}
        {r.depth_mm !== null && r.depth_mm !== undefined && <> · {t('measures.atDepth', { mm: r.depth_mm })}</>}
        {/* M16-D: the cell of the club's grid a reading was taken in (a bunker walk) */}
        {r.position_label && <> · <span data-testid="reading-cell">{t('measures.cell', { label: r.position_label })}</span></>}
        {r.method && <> · {r.method}</>}{r.instrument && <> · {r.instrument}</>}
        {who && <> · {t('measures.by', { who })}</>}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {r._pending && <Pill tone="amber">{t('measures.pill.pending')}</Pill>}
        {r.source === 'import' && <Pill tone="gray">{t('measures.pill.imported')}</Pill>}
        {r.corrects_measurement_id && <Pill tone="gray">{t('measures.pill.correction')}</Pill>}
        {manage.allowed && !r._pending && !r.superseded_by_id && <button className="btn !min-h-9 !px-3 text-sm" onClick={() => setOpen((v) => !v)} data-testid="reading-correct">{t('measures.correct')}</button>}
      </div>
      {r.note && <p className="meta">{r.note}</p>}
      {r.correction_reason && <p className="meta">{t('measures.reason')} : {r.correction_reason}</p>}
      {open && <CorrectForm r={r} close={() => { setOpen(false); onChanged?.() }} />}
    </li>
  )
}

function CorrectForm({ r, close }: { r: Pending<MeasurementRow>; close: () => void }) {
  const { t, i18n } = useTranslation()
  const act = useWorkshopAction()
  const id = useRef(newId())
  const [value, setValue] = useState(String(r.value_entered))
  const [unit, setUnit] = useState(r.unit_entered)
  const [reason, setReason] = useState('')
  const check = checkForm({ type: r.measurement_type, value, unit, hasArea: !!r.area_id, depth: r.depth_mm === null || r.depth_mm === undefined ? '' : String(r.depth_mm) })
  return (
    <form className="mt-2 flex flex-col gap-3 border-t pt-3" data-testid="correct-form" onSubmit={(e) => {
      e.preventDefault()
      void act.run('measurement.correct@1', { measurement_id: id.current, corrects_measurement_id: r.id, value: Number(value.replace(',', '.')), unit, reason: reason.trim() }, 'measures.corrected').then((ok) => { if (ok) close() })
    }}>
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <Field label={t('measures.value')} htmlFor={`cv-${r.id}`}><input id={`cv-${r.id}`} className="input" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} data-testid="correct-value" /></Field>
        <Field label={t('measures.unit')} htmlFor={`cu-${r.id}`}><select id={`cu-${r.id}`} className="input" value={unit} onChange={(e) => setUnit(e.target.value)}>{unitsFor(r.measurement_type, r.unit_entered).map((u) => <option key={u.code} value={u.code}>{u.symbol[lang2(i18n.language)]}</option>)}</select></Field>
      </div>
      <Field label={t('measures.reason')} htmlFor={`cr-${r.id}`}><input id={`cr-${r.id}`} className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correct-reason" /></Field>
      {!check.ok && check.code && <Banner tone="warn">{errorText(t, check.code)}</Banner>}
      {act.pending && <PendingBanner />}
      {act.error && <Banner tone="error"><span data-testid="correct-error">{act.error}</span></Banner>}
      <button className="btn btn-primary" disabled={act.busy || !check.ok || reason.trim() === ''} data-testid="correct-save">{t('measures.correctSave')}</button>
    </form>
  )
}
